import { X, ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createDepartment, getOrganizations, getBranches } from '../../services/departmentService';
import { toast } from 'react-toastify';
import './styles/Department.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

const AddDepartmentDrawer = ({ open, onClose }: Props) => {
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    organizationId: '',
    branchId: '',
    code: '',
    title: '',
    description: '',
    sourceLanguageId: '',
    isSystem: true,
  });

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const data = await getOrganizations();
      setOrganizations(data);
    } catch (error) {
      console.log(error);
    }
  };

  const loadBranches = async (organizationId: string) => {
    try {
      const data = await getBranches(organizationId);

      console.log('Branches Response:', data);

      setBranches(data);
    } catch (error) {
      console.log('Branch Error:', error);
      setBranches([]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'organizationId') {
      setFormData((prev) => ({
        ...prev,
        organizationId: value,
        branchId: '',
      }));

      if (value) {
        loadBranches(value);
      } else {
        setBranches([]);
      }
    }
  };

  const handleCreateDepartment = async () => {
    try {
      await createDepartment({
        organizationId: formData.organizationId,
        branchId: formData.branchId,
        code: formData.code,
        title: formData.title,
        description: formData.description,
        sourceLanguageId: formData.sourceLanguageId === '' ? null : formData.sourceLanguageId,
        isSystem: formData.isSystem,
      });

      setFormData({
        organizationId: '',
        branchId: '',
        code: '',
        title: '',
        description: '',
        sourceLanguageId: '',
        isSystem: true,
      });

      setBranches([]);

      toast.success('Department created successfully');

      onClose();
    } catch (error) {
      console.log(error);
      toast.error('Failed to create department');
    }
  };

  return (
    <div className={`drawer-overlay ${open ? 'show' : ''}`}>
      <div className={`department-drawer ${open ? 'open' : ''}`}>
        {/* ========== HEADER ========== */}
        <div className="drawer-header">
          <h2 className="drawer-title">Add Department</h2>

          <div className="drawer-header-right">
            <div className="drawer-avatar">D</div>

            <button onClick={onClose} className="drawer-close" type="button">
              <X size={18} strokeWidth={2.2} />
            </button>
          </div>
        </div>

        {/* ========== BODY (scrollable) ========== */}
        <div className="drawer-body">
          {/* Department Type */}
          <div className="form-section">
            <label className="form-label">Department Type</label>
            <div className="type-buttons">
              <button type="button" className="type-btn active">
                New Department
              </button>
              <button type="button" className="type-btn">
                Existing Department
              </button>
            </div>
            <p className="form-hint">Create a new department or link to an existing one.</p>
          </div>

          {/* Department Name */}
          <div className="form-section">
            <label className="form-label">
              Title <span className="required">*</span>
            </label>
            <input className="form-input" name="title" value={formData.title} onChange={handleChange} placeholder="Enter Title" />
          </div>

          {/* Organization */}
          <div className="form-section">
            <label className="form-label">
              Organization <span className="required">*</span>
            </label>
            <div className="form-select-wrapper">
              <select className="form-select" name="organizationId" value={formData.organizationId} onChange={handleChange}>
                <option value="">Select Organization</option>

                {organizations.map((item: any) => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="select-chevron" />
            </div>
          </div>

          {/* Branch */}
          <div className="form-section">
            <label className="form-label">Branch</label>
            <div className="form-select-wrapper">
              <select
                className="form-select"
                name="branchId"
                value={formData.branchId}
                onChange={handleChange}
                disabled={!formData.organizationId}
              >
                <option value="">Select Branch</option>

                {branches.map((item: any) => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="select-chevron" />
            </div>
          </div>

          {/* Description */}
          <div className="form-section">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter Description"
            />
          </div>

          {/* Department Code */}
          <div className="form-section">
            <label className="form-label">Department Code</label>
            <input className="form-input" name="code" value={formData.code} onChange={handleChange} placeholder="Department Code" />
          </div>

          {/* Source Language */}
          <div className="form-section">
            <label className="form-label">Source Language</label>
            <div className="form-select-wrapper">
              <select className="form-select" name="sourceLanguageId" value={formData.sourceLanguageId} onChange={handleChange}>
                <option value="">Select Language</option>
                <option value="english">English</option>
                <option value="hindi">Hindi</option>
                <option value="arabic">Arabic</option>
              </select>
              <ChevronDown size={16} className="select-chevron" />
            </div>
          </div>

          {/* Status */}
          <div className="form-section">
            <label className="form-label">Status</label>
            <div className="type-buttons">
              <button
                type="button"
                className={`type-btn ${formData.isSystem ? 'active' : ''}`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    isSystem: true,
                  }))
                }
              >
                Active
              </button>

              <button
                type="button"
                className={`type-btn ${!formData.isSystem ? 'active' : ''}`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    isSystem: false,
                  }))
                }
              >
                Inactive
              </button>
            </div>
          </div>
        </div>

        {/* ========== FOOTER (always visible) ========== */}
        <div className="drawer-footer">
          <button type="button" className="create-btn" onClick={handleCreateDepartment}>
            <span>Create Department</span>
            <span className="create-btn-icon">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.5 6H9.5M6 2.5V9.5" stroke="#FAFBFD" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddDepartmentDrawer;
